import useSWR from 'swr';
import type { RequestFailure } from '@ff-it/api';
import { fetcher } from 'services';
import type { ActivityType } from 'types';
import type { ProductListProduct } from './productListColumns';

export type ProductCollectionResponse<TProduct> = TProduct[] | { results: TProduct[] };

export type ProductCollection<TProduct> = {
  products: TProduct[];
  isLoading: boolean;
  error: RequestFailure<unknown> | undefined;
  mutate: () => Promise<unknown>;
};

function productsFromResponse<TProduct>(response: ProductCollectionResponse<TProduct>): TProduct[] {
  return Array.isArray(response) ? response : response.results;
}

/** Fetch a bounded, complete product collection for one fixed activity type. */
export function useProductCollection<TProduct extends ProductListProduct>(
  endpoint: string,
  type: ActivityType,
): ProductCollection<TProduct> {
  const { data, error, isLoading, mutate } = useSWR<ProductCollectionResponse<TProduct>, RequestFailure<unknown>>(
    {
      url: endpoint,
      method: 'GET',
      // An empty page_size explicitly opts out of the default DRF pagination.
      queryParams: { type, page_size: '' },
    },
    fetcher,
  );

  return {
    products: data ? productsFromResponse(data) : [],
    isLoading,
    error,
    mutate: () => mutate(),
  };
}
